/**
 * Finanzas → Control Efectivo (`/finanzas/control-efectivo`).
 *
 * Portado de `Cash::index` + `views/cash/index.php` y de su bundle
 * `src/cash/index.js`, que pedía los datos a la API interna `API/Cash` (modelo
 * `Cash_model`) y al controlador `Efectivo` (que es donde se registran los
 * depósitos y los egresos).
 *
 * La pantalla lleva el efectivo de las bóvedas de los turnos hacia el banco:
 * arriba los cuatro contadores (procesado, confirmado, egresos y pendiente por
 * depositar), los dos botones para **crear la ficha de depósito** —por totales de
 * turno o por bóvedas detalladas— y debajo el histórico de fichas, cada una con
 * sus bóvedas y sus depósitos.
 *
 * Reglas heredadas:
 * - Sólo se ofrecen bóvedas **activas**, de turnos **cerrados** y pendientes de
 *   procesar; al crear la ficha pasan a procesadas y al anularla vuelven atrás.
 * - Los contadores y el pie suman únicamente las fichas **activas**.
 * - La ficha se anula sólo mientras no tenga depósitos.
 * - La carga de bóvedas y el registro de depósitos van cada uno en una
 *   transacción del lado del servidor.
 */

import {
  ArrowDownLeft,
  Ban,
  CheckCircle2,
  Layers,
  ListChecks,
  RefreshCw,
  Repeat,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatCurrency, formatDateValue } from '@/lib/utils';

import {
  useAnularFichaEfectivo,
  useFichasEfectivo,
} from '../control-efectivo.api';
import {
  DialogoBovedasFicha,
  DialogoDepositarDetallado,
  DialogoDepositarTurno,
  DialogoDepositosFicha,
} from '../components/control-efectivo-dialogos';
import type { FichaEfectivo } from '../control-efectivo.types';

const TAMANOS = [10, 25, 50, 100];

type DialogoAbierto = 'bovedas' | 'depositos' | 'turno' | 'detallado' | null;

/** Color del distintivo del estado de la ficha. */
function tonoFicha(estado: string | null) {
  if (estado === 'deposited') return 'success' as const;
  if (estado === 'partially_deposited') return 'warning' as const;

  return 'brand' as const;
}

export function ControlEfectivoPage() {
  useDocumentTitle('Finanzas · Control Efectivo');

  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [dialogo, setDialogo] = useState<DialogoAbierto>(null);
  const [ficha, setFicha] = useState<FichaEfectivo | null>(null);
  const [porAnular, setPorAnular] = useState<FichaEfectivo | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const consulta = useFichasEfectivo(pagina, limite);
  const anular = useAnularFichaEfectivo();

  const datos = consulta.data;
  const fichas = datos?.fichas ?? [];
  const totales = datos?.totales;
  const permisos = datos?.permisos;
  const pendientes = datos?.pendientes;
  const total = datos?.total ?? 0;
  const paginas = datos?.paginas ?? 1;

  const avisar = (mensaje: string) => {
    setAviso(mensaje);
    setError(null);
    setTimeout(() => setAviso(null), 8000);
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);
    anular.mutate(porAnular.id, {
      onSuccess: (respuesta) => {
        avisar(respuesta.message);
        setPorAnular(null);
      },
      onError: (fallo) => {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo anular la ficha.');
        setPorAnular(null);
      },
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Control Efectivo</h1>
          <p className="mt-1 text-sm text-ink-500">
            El efectivo de las bóvedas de los turnos hasta el banco: se agrupa en una ficha de
            depósito y contra ella se registran los depósitos a cuenta y los egresos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo leer el control de efectivo">
          Vuelve a intentarlo en unos segundos.
        </Alert>
      ) : null}

      {/* Contadores: lo procesado, lo confirmado, los egresos y lo pendiente. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700"
            >
              <Wallet className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Total procesado
              </p>
              <p className="text-base font-bold break-words text-brand-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.procesado ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.fichas ?? 0).toLocaleString('es-PE')} ficha(s) activa(s)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"
            >
              <CheckCircle2 className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Total confirmado
              </p>
              <p className="text-base font-bold break-words text-emerald-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.confirmado ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"
            >
              <ArrowDownLeft className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Total egresos
              </p>
              <p className="text-base font-bold break-words text-rose-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.egresos ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-amber-50/60 sm:col-span-2 xl:col-span-1">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700"
            >
              <Repeat className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Pendiente por depositar
              </p>
              <p className="text-base font-bold break-words text-amber-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.por_depositar ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operaciones */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          {permisos?.depositar_turno ? (
            <Button
              size="sm"
              leftIcon={<Layers className="size-4" aria-hidden="true" />}
              onClick={() => {
                setError(null);
                setDialogo('turno');
              }}
            >
              Depositar por totales de turno
            </Button>
          ) : null}

          {permisos?.depositar_detallado ? (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ListChecks className="size-4" aria-hidden="true" />}
              onClick={() => {
                setError(null);
                setDialogo('detallado');
              }}
            >
              Depositar por bóvedas detalladas
            </Button>
          ) : null}

          <span className="ml-auto text-xs text-ink-500">
            Pendientes: {(pendientes?.por_turno.turnos ?? 0).toLocaleString('es-PE')} turno(s) ·{' '}
            {formatCurrency(pendientes?.por_turno.total ?? 0)} ·{' '}
            {(pendientes?.detallado.bovedas ?? 0).toLocaleString('es-PE')} bóveda(s)
          </span>
        </CardContent>
      </Card>

      {/* Histórico */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-ink-800">Histórico de depósitos</h2>

            <label className="flex items-center gap-2 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => {
                  setPagina(1);
                  setLimite(Number(evento.target.value));
                }}
                className="h-9 w-20 rounded-control border border-ink-200 bg-white px-2 text-sm"
                aria-label="Filas por página"
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">#</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-2 py-2 whitespace-nowrap">Tipo</th>
                  <th className="px-2 py-2 whitespace-nowrap">Estado</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Procesado</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Confirmado</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Egreso</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">A depositar</th>
                  <th className="px-2 py-2 whitespace-nowrap">Estatus depósito</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Bóvedas</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Depósitos</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 12 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : fichas.map((item) => (
                      <tr
                        key={item.id}
                        className={cn(
                          'border-t border-ink-100',
                          item.estado === 2 && 'text-ink-500 line-through',
                        )}
                      >
                        <td className="px-2 py-2 text-ink-500 tabular-nums">{item.id}</td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          {item.fecha ? formatDateValue(item.fecha) : '—'}
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={item.tipo === 'detallado' ? 'brand' : 'neutral'}>
                            {item.tipo_etiqueta}
                          </Badge>
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={item.estado === 1 ? 'success' : 'danger'}>
                            {item.estado_etiqueta}
                          </Badge>
                        </td>

                        <td className="px-2 py-2 text-right whitespace-nowrap tabular-nums">
                          {formatCurrency(item.total_depositar)}
                        </td>

                        <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-700 tabular-nums">
                          {formatCurrency(item.total_confirmado)}
                        </td>

                        <td className="px-2 py-2 text-right whitespace-nowrap text-rose-600 tabular-nums">
                          {formatCurrency(item.total_gastos)}
                        </td>

                        <td className="px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums">
                          {formatCurrency(item.total_por_depositar)}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <Badge variant={tonoFicha(item.estado_deposito)}>
                            {item.estado_deposito_etiqueta}
                          </Badge>
                        </td>

                        <td className="px-2 py-2 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Ver las bóvedas de la ficha"
                            aria-label={`Ver las bóvedas de la ficha ${item.id}`}
                            onClick={() => {
                              setFicha(item);
                              setDialogo('bovedas');
                            }}
                          >
                            Bóvedas
                          </Button>
                        </td>

                        <td className="px-2 py-2 text-center">
                          {item.estado === 1 ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Ver los depósitos de la ficha"
                              aria-label={`Ver los depósitos de la ficha ${item.id}`}
                              onClick={() => {
                                setFicha(item);
                                setDialogo('depositos');
                              }}
                            >
                              Depósitos
                            </Button>
                          ) : (
                            <span className="text-ink-300">—</span>
                          )}
                        </td>

                        <td className="px-2 py-2 text-right">
                          {item.estado === 1 && permisos?.eliminar_registro ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Anular la ficha de depósito"
                              aria-label={`Anular la ficha de depósito ${item.id}`}
                              onClick={() => {
                                setError(null);
                                setPorAnular(item);
                              }}
                            >
                              <Ban className="size-4 text-rose-600" aria-hidden="true" />
                            </Button>
                          ) : (
                            <span className="text-ink-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
              </tbody>

              <tfoot className="bg-ink-50/60 font-semibold text-ink-800">
                <tr className="border-t border-ink-100">
                  <td className="px-2 py-2 text-right" colSpan={4}>
                    Totales de las fichas activas
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatCurrency(totales?.procesado ?? 0)}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-700 tabular-nums">
                    {formatCurrency(totales?.confirmado ?? 0)}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap text-rose-600 tabular-nums">
                    {formatCurrency(totales?.egresos ?? 0)}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatCurrency(totales?.por_depositar ?? 0)}
                  </td>
                  <td className="px-2 py-2" colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                onClick={() => setPagina((actual) => Math.min(paginas, actual + 1))}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diálogos */}
      <DialogoBovedasFicha
        abierto={dialogo === 'bovedas'}
        ficha={ficha}
        onCerrar={() => setDialogo(null)}
      />

      <DialogoDepositosFicha
        abierto={dialogo === 'depositos'}
        ficha={ficha}
        onAviso={avisar}
        onCerrar={() => setDialogo(null)}
      />

      <DialogoDepositarTurno
        abierto={dialogo === 'turno'}
        onCerrar={() => setDialogo(null)}
        onProcesado={(mensaje) => {
          setDialogo(null);
          avisar(mensaje);
        }}
      />

      <DialogoDepositarDetallado
        abierto={dialogo === 'detallado'}
        onCerrar={() => setDialogo(null)}
        onProcesado={(mensaje) => {
          setDialogo(null);
          avisar(mensaje);
        }}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title={`¿Anular la ficha de depósito #${porAnular?.id ?? ''}?`}
        description={
          'Sus bóvedas volverán a quedar pendientes de depósito y los movimientos bancarios ' +
          'que hubiera creado quedarán anulados. Sólo se puede anular mientras la ficha no ' +
          'tenga depósitos.'
        }
        confirmLabel="Anular ficha"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
